import { useState } from 'react';
import { 
  Moon, 
  Sun,
  Bell,
  Shield,
  Database,
  HelpCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export function Settings() {
  const [theme, setTheme] = useState(() => {
    return document.documentElement.getAttribute('data-theme') || 'light';
  });
  const [notifications, setNotifications] = useState({
    email: true,
    browser: false,
    todoReminder: true,
    dealUpdate: true,
  });

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          設定
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          アプリケーションの設定を管理
        </p>
      </div>

      {/* テーマ設定 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
            外観
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <button
              onClick={() => handleThemeChange('light')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                theme === 'light' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
            >
              <Sun className="mx-auto mb-2" size={24} />
              <p className="font-medium">ライト</p>
            </button>
            <button
              onClick={() => handleThemeChange('dark')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                theme === 'dark' ? 'border-blue-500 bg-blue-900' : 'border-gray-200'
              }`}
            >
              <Moon className="mx-auto mb-2" size={24} />
              <p className="font-medium">ダーク</p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* 通知設定 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell size={20} />
            通知
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>メール通知</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                重要な更新をメールで受け取る
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.email}
                onChange={(e) => setNotifications({ ...notifications, email: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>ブラウザ通知</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                デスクトップ通知を表示する
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.browser}
                onChange={(e) => setNotifications({ ...notifications, browser: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>ToDoリマインダー</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                期限間近のToDoを通知
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.todoReminder}
                onChange={(e) => setNotifications({ ...notifications, todoReminder: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>案件更新通知</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                担当案件の更新を通知
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.dealUpdate}
                onChange={(e) => setNotifications({ ...notifications, dealUpdate: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* データ管理 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database size={20} />
            データ管理
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>データエクスポート</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                すべてのデータをCSV形式でエクスポート
              </p>
            </div>
            <Button variant="outline">エクスポート</Button>
          </div>
        </CardContent>
      </Card>

      {/* セキュリティ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield size={20} />
            セキュリティ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>パスワード変更</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                定期的なパスワード変更を推奨
              </p>
            </div>
            <Button variant="outline" onClick={() => window.location.href = '/change-password'}>
              変更する
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>ログインセッション</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                現在アクティブなセッションを管理
              </p>
            </div>
            <Button variant="outline">セッション管理</Button>
          </div>
        </CardContent>
      </Card>

      {/* ヘルプ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle size={20} />
            ヘルプ & サポート
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a 
              href="#" 
              className="p-4 rounded-lg border hover:bg-gray-50 transition-colors"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>ドキュメント</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                使い方ガイドとFAQ
              </p>
            </a>
            <a 
              href="#" 
              className="p-4 rounded-lg border hover:bg-gray-50 transition-colors"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>お問い合わせ</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                サポートチームに連絡
              </p>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* バージョン情報 */}
      <div className="text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
        <p>T's FUTURE 営業管理システム v1.0.0</p>
        <p>© T's FUTURE Co., Ltd. All rights reserved.</p>
      </div>
    </div>
  );
}
